import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import { YMaps, Map, Polygon } from '@pbe/react-yandex-maps'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'

const EMPTY_ZONE = {
	name: '',
	color: '#3388ff',
	pos_id: null,
	coordinates: [],
	delivery_price: 0,
	delivery_minutes: 60,
	min_order_amount: 0,
	is_active: true,
}

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)
	const ymapsRef = useRef(null)
	const mapRef = useRef(null)
	const editorPolygonRef = useRef(null)

	const [zones, setZones] = useState([])
	const [poses, setPoses] = useState([])
	const [selectedItem, setSelectedItem] = useState({ ...EMPTY_ZONE })
	const [drawing, setDrawing] = useState(false)

	const mapCenter = poses[0]?.latitude && poses[0]?.longitude
		? [Number(poses[0].latitude), Number(poses[0].longitude)]
		: [41.311081, 69.240562]

	async function searchWithFilter() {
		const response = await GET('/chain/delivery-zones', {}, { loader: true })
		if (httpOk(response)) setZones(response.data || [])
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data || [])
	}

	function clearEditorPolygon() {
		const map = mapRef.current
		if (map && editorPolygonRef.current) {
			map.geoObjects.remove(editorPolygonRef.current)
			editorPolygonRef.current = null
		}
	}

	function selectZone(zone) {
		clearEditorPolygon()
		setDrawing(false)
		setSelectedItem({
			...EMPTY_ZONE,
			...zone,
			coordinates: Array.isArray(zone.coordinates) ? zone.coordinates : [],
		})
	}

	function createNew() {
		clearEditorPolygon()
		setDrawing(false)
		setSelectedItem({ ...EMPTY_ZONE })
	}

	function syncCoordinatesFromPolygon() {
		if (!editorPolygonRef.current) return
		const ring = editorPolygonRef.current.geometry.getCoordinates()?.[0] || []
		setSelectedItem(prev => ({ ...prev, coordinates: ring }))
	}

	function startDraw() {
		const ymaps = ymapsRef.current
		const map = mapRef.current
		if (!ymaps || !map) return

		clearEditorPolygon()
		const existing = selectedItem.coordinates?.length >= 3 ? [selectedItem.coordinates] : []
		const polygon = new ymaps.Polygon(existing, {}, {
			editorDrawingCursor: 'crosshair',
			fillColor: `${selectedItem.color || '#3388ff'}55`,
			strokeColor: selectedItem.color || '#3388ff',
			strokeWidth: 3,
		})
		map.geoObjects.add(polygon)
		editorPolygonRef.current = polygon

		if (existing.length) {
			polygon.editor.startEditing()
		} else {
			polygon.editor.startDrawing()
		}
		polygon.editor.events.add(['drawingstop', 'vertexdragend', 'vertexadd'], syncCoordinatesFromPolygon)
		setDrawing(true)
	}

	function stopDraw() {
		if (editorPolygonRef.current?.editor) {
			editorPolygonRef.current.editor.stopDrawing()
			editorPolygonRef.current.editor.stopEditing()
		}
		syncCoordinatesFromPolygon()
		setDrawing(false)
	}

	async function createUpdate(e) {
		e.preventDefault()
		if (drawing) stopDraw()

		const payload = { ...selectedItem }
		if (editorPolygonRef.current) {
			payload.coordinates = editorPolygonRef.current.geometry.getCoordinates()?.[0] || payload.coordinates
		}

		const response = payload.id
			? await PUT('/chain/delivery-zones', payload, { loader: true })
			: await POST('/chain/delivery-zones', payload, { loader: true })

		if (httpOk(response)) {
			clearEditorPolygon()
			setDrawing(false)
			await searchWithFilter()
			if (!payload.id && response.data?.id) {
				setSelectedItem({ ...payload, id: Number(response.data.id) })
			}
		}
	}

	async function removeZone() {
		if (!selectedItem.id) return
		const response = await DELETE(`/chain/delivery-zones/${selectedItem.id}`, {}, { loader: true })
		if (httpOk(response)) {
			createNew()
			searchWithFilter()
		}
	}

	useEffect(() => {
		searchWithFilter()
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('delivery_zones')}</h4>
				<button className="btn btn-primary" onClick={createNew}>{t('create')}</button>
			</div>

			<form className="card mb-2" onSubmit={createUpdate} autoComplete="off">
				<div className="card-body py-2">
					<div className="d-flex flex-wrap gap-2 mb-2" style={{ maxHeight: 72, overflow: 'auto' }}>
						{zones.map((zone) => (
							<div
								key={zone.id}
								className={`px-2 py-1 rounded cursor ${selectedItem.id === zone.id ? 'bg-light' : ''}`}
								style={{ borderLeft: `4px solid ${zone.color || '#3388ff'}`, opacity: zone.is_active ? 1 : 0.5, whiteSpace: 'nowrap' }}
								onClick={() => selectZone(zone)}>
								<span className="fw-600">{zone.name}</span>
								<span className="small text-muted ms-2">
									{zone.pos_name || '—'} · {formatMoney(zone.delivery_price)}
								</span>
							</div>
						))}
						{!zones.length &&
							<span className="text-muted">{t('list_empty')}</span>
						}
					</div>
					<div className="d-flex flex-wrap align-items-end gap-3">
						<div className="form-group mb-0" style={{ width: 180 }}>
							<label>{t('name')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" value={selectedItem.name}
								onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })} />
						</div>
						<div className="form-group mb-0" style={{ width: 180 }}>
							<label>{t('pos')}</label>
							<Select
								isClearable
								options={poses}
								value={poses.find((option) => option.id === selectedItem.pos_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, pos_id: option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group mb-0" style={{ width: 48 }}>
							<label className="text-nowrap">{t('zone_color')}</label>
							<label className="mb-0 d-block position-relative"
								style={{
									width: 38,
									height: 38,
									borderRadius: 6,
									border: '1px solid var(--dark-border-strong, #ced4da)',
									background: selectedItem.color || '#3388ff',
									cursor: 'pointer',
								}}>
								<input type="color"
									style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
									value={selectedItem.color || '#3388ff'}
									onChange={(e) => setSelectedItem({ ...selectedItem, color: e.target.value })} />
							</label>
						</div>
						<div className="form-group mb-0" style={{ width: 130 }}>
							<label className="text-nowrap">{t('delivery_price')}</label>
							<input type="text" className="form-control"
								value={selectedItem.delivery_price ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, delivery_price: e.target.value.replace(/[^0-9.]/g, '') })} />
						</div>
						<div className="form-group mb-0" style={{ width: 160 }}>
							<label className="text-nowrap">{t('delivery_minutes')}</label>
							<input type="text" className="form-control"
								value={selectedItem.delivery_minutes ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, delivery_minutes: e.target.value.replace(/[^0-9]/g, '') })} />
						</div>
						<div className="form-group mb-0" style={{ width: 190 }}>
							<label className="text-nowrap">{t('min_order_amount')}</label>
							<input type="text" className="form-control"
								value={selectedItem.min_order_amount ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, min_order_amount: e.target.value.replace(/[^0-9.]/g, '') })} />
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
						<div className="form-check mb-0">
							<input type="checkbox" className="form-check-input" id="zone-active"
								checked={!!selectedItem.is_active}
								onChange={(e) => setSelectedItem({ ...selectedItem, is_active: e.target.checked })} />
							<label className="form-check-label" htmlFor="zone-active">{t('active')}</label>
						</div>
						<div className="d-flex align-items-center gap-2">
							<button type="button" className="btn btn-outline-primary"
								onClick={drawing ? stopDraw : startDraw}>
								{drawing ? t('finish_drawing') : t('draw_zone')}
							</button>
							{selectedItem.id &&
								<button type="button" className="btn btn-outline-danger" onClick={removeZone}>
									{t('delete')}
								</button>
							}
							<button type="submit" className="btn btn-primary"
								disabled={!selectedItem.name || (selectedItem.coordinates || []).length < 3}>
								{t('save')}
							</button>
						</div>
					</div>
					<div className="small text-muted mt-1">
						{t('delivery_zones_hint')}
						{selectedItem.coordinates?.length ? ` (${selectedItem.coordinates.length})` : ''}
					</div>
				</div>
			</form>

			<div className="card">
				<div className="card-body p-0" style={{ height: 'calc(100vh - 280px)', minHeight: 520 }}>
					<YMaps query={{ load: 'package.full', apikey: reduxAccount.map_token || 'b5de12cc-6217-4b13-9078-2e99c84df330' }}>
						<Map
							width="100%"
							height="100%"
							style={{ width: '100%', height: '100%', minHeight: 520 }}
							defaultState={{ center: mapCenter, zoom: 12 }}
							instanceRef={(ref) => { mapRef.current = ref }}
							onLoad={(ymaps) => { ymapsRef.current = ymaps }}
							options={{ yandexMapDisablePoiInteractivity: true }}>
							{zones.filter((z) => z.id !== selectedItem.id || !drawing).map((zone) => (
								Array.isArray(zone.coordinates) && zone.coordinates.length >= 3 &&
								<Polygon
									key={zone.id}
									geometry={[zone.coordinates]}
									options={{
										fillColor: `${zone.color || '#3388ff'}44`,
										strokeColor: zone.color || '#3388ff',
										strokeWidth: selectedItem.id === zone.id ? 3 : 2,
									}}
									onClick={() => selectZone(zone)}
								/>
							))}
						</Map>
					</YMaps>
				</div>
			</div>
		</>
	)
}

export default Index
